/**
 * WHO SOMEBODY IS TO SOMEBODY ELSE
 * ================================
 *
 * MP holds relationships as a row on one person pointing at another, and the
 * word for it depends on THREE facts, not one: the relationship, and the
 * gender of the person being described. `Contact_Relationships` row
 * (Contact_ID = Rosie, Related_Contact_ID = Sarah, Relationship_ID = 5
 * "Child of") says Rosie is Sarah's child, so Sarah is Rosie's parent — and
 * Sarah's `Gender_ID` is what turns parent into mother.
 *
 * So the ids here are keyed the way the reading goes: `RELATIONS[5]` is what
 * the RELATED person is, because that is the direction a sentence runs.
 * `apps/api/src/lib/events/household.ts` keys its list the same way, and the
 * two must stay in step.
 *
 * WHY ASK IT GENDERED. A form that asks "Parent" and then asks gender is two
 * questions for one fact. "Mom" answers both, and the answer is worth keeping:
 * it fills in a blank on a real MP record rather than living and dying in one
 * registration's answers.
 */

export type Gender = 'male' | 'female' | null;

export interface RelationWord {
  neutral: string;
  male: string;
  female: string;
  /**
   * What MP's own dropdown calls this, so the form field still gets a value
   * from its own list. Matched case-insensitively; anything unmatched falls
   * back to Other.
   */
  option: string;
}

/** Relationship_ID on the registrant's row -> what the other person IS. */
export const RELATIONS: Record<number, RelationWord> = {
  1: { neutral: 'spouse', male: 'husband', female: 'wife', option: 'Spouse' },
  2: { neutral: 'sibling', male: 'brother', female: 'sister', option: 'Other' },
  3: { neutral: 'niece or nephew', male: 'nephew', female: 'niece', option: 'Other' },
  4: { neutral: 'aunt or uncle', male: 'uncle', female: 'aunt', option: 'Aunt or Uncle' },
  5: { neutral: 'parent', male: 'father', female: 'mother', option: 'Parent' },
  6: { neutral: 'child', male: 'son', female: 'daughter', option: 'Other' },
  9: { neutral: 'friend', male: 'friend', female: 'friend', option: 'Friend' },
  12: { neutral: 'grandchild', male: 'grandson', female: 'granddaughter', option: 'Other' },
  13: { neutral: 'grandparent', male: 'grandfather', female: 'grandmother', option: 'Grandparent' },
  17: { neutral: 'neighbor', male: 'neighbor', female: 'neighbor', option: 'Neighbor' },
  18: { neutral: 'cousin', male: 'cousin', female: 'cousin', option: 'Other' },
  29: { neutral: 'legal ward', male: 'legal ward', female: 'legal ward', option: 'Other' },
  30: { neutral: 'guardian', male: 'guardian', female: 'guardian', option: 'Guardian' },
  39: { neutral: 'step-sibling', male: 'step-brother', female: 'step-sister', option: 'Other' },
  40: { neutral: 'step-parent', male: 'step-father', female: 'step-mother', option: 'Parent' },
  41: { neutral: 'step-child', male: 'step-son', female: 'step-daughter', option: 'Other' },
};

/** The word for what the related person is, as gendered as the record allows. */
export function wordFor(relationshipId: number | null, gender: Gender): string | null {
  if (!relationshipId) return null;
  const words = RELATIONS[relationshipId];
  if (!words) return null;
  return gender === 'male' ? words.male : gender === 'female' ? words.female : words.neutral;
}

/** True when knowing the gender would change the word we say. */
export function needsGender(relationshipId: number | null): boolean {
  if (!relationshipId) return false;
  const words = RELATIONS[relationshipId];
  // `!= null` rather than Boolean(): it narrows, which is the whole point.
  return words != null && words.male !== words.female;
}

export interface RelationChoice {
  /** Relationship_ID to write on the registrant's row. Null is "someone else". */
  relationshipId: number | null;
  /** What this choice also tells us about them. */
  gender: Gender;
  label: string;
}

/**
 * The chips, in the order a student ministry event actually needs them. Each
 * one carries a gender where the word carries a gender, which is how one tap
 * answers two questions.
 */
export const RELATION_CHOICES: RelationChoice[] = [
  { relationshipId: 5, gender: 'female', label: 'Mom' },
  { relationshipId: 5, gender: 'male', label: 'Dad' },
  { relationshipId: 13, gender: 'female', label: 'Grandma' },
  { relationshipId: 13, gender: 'male', label: 'Grandpa' },
  { relationshipId: 4, gender: 'female', label: 'Aunt' },
  { relationshipId: 4, gender: 'male', label: 'Uncle' },
  { relationshipId: 2, gender: 'female', label: 'Sister' },
  { relationshipId: 2, gender: 'male', label: 'Brother' },
  { relationshipId: 1, gender: 'female', label: 'Wife' },
  { relationshipId: 1, gender: 'male', label: 'Husband' },
  { relationshipId: 30, gender: null, label: 'Guardian' },
  { relationshipId: 9, gender: null, label: 'Friend' },
  { relationshipId: 17, gender: null, label: 'Neighbor' },
  // No "Someone else" here, unlike RELATION_CHOICES. On the emergency-contact
  // question that phrase is a real answer — it writes Other into MP's own
  // dropdown. Here it wrote nothing at all: a chip that looked like an answer,
  // recorded no relationship, and left the page looking answered. Skipping is
  // the same outcome and says so.
];

/**
 * THE CHIPS WORTH OFFERING FOR A PARTICULAR PERSON.
 *
 * Half of these are the same relationship said two ways — Mom and Dad are both
 * Relationship 5, Wife and Husband are both 1 — and the word carries a gender
 * so that one tap answers two questions at once. Where MP already knows the
 * gender, the second question is already answered, and offering "Dad" for a
 * woman the church has on file as female is offering a wrong answer.
 *
 * So a known gender halves the list: the words that fit, plus the ones that
 * never carried a gender at all. Guardian, Friend and Neighbour stay for
 * everybody, because they are true of anyone.
 *
 * An unknown gender changes nothing. Every chip stays, and picking one is
 * still how we learn it — that is the whole reason these are worded this way.
 */
export function choicesForGender(gender: Gender): RelationChoice[] {
  if (!gender) return RELATION_CHOICES;
  return RELATION_CHOICES.filter((c) => !c.gender || c.gender === gender);
}

/** The two ways one known relationship can be said, for the gender question. */
export function genderChoices(relationshipId: number): RelationChoice[] {
  return RELATION_CHOICES.filter((c) => c.relationshipId === relationshipId && c.gender);
}

/**
 * The value to write into MP's own relationship dropdown. The list differs by
 * form and by year — Spouse was on last year's and is not on this one — so
 * anything we cannot match lands on Other rather than writing a value the
 * field does not offer.
 */
export function formValueFor(relationshipId: number | null, options: string[]): string {
  const wanted = relationshipId ? RELATIONS[relationshipId]?.option : 'Other';
  const hit = options.find((o) => o.toLowerCase() === (wanted ?? 'Other').toLowerCase());
  if (hit) return hit;
  return options.find((o) => o.toLowerCase() === 'other') ?? '';
}

/** Read a stored MP answer back into a relationship, for a record with none. */
export function relationshipFromOption(option: string): number | null {
  const want = option.trim().toLowerCase();
  if (!want) return null;
  for (const [id, words] of Object.entries(RELATIONS))
    if (words.option.toLowerCase() === want) return Number(id);
  return null;
}

/**
 * A fact learned while registering, on its way to the record it belongs on.
 *
 * `relationshipId` null is "someone else": we were told there is no word for
 * it, which is worth not asking again but is not worth a row in MP.
 */
export interface LearnedRelationship {
  /** The registrant, on whose row the relationship is written. */
  contactId: number;
  /** Their emergency contact. */
  relatedContactId: number;
  relationshipId: number | null;
  gender: Gender;
}

/**
 * THE SAME QUESTION, ASKED OF A DIFFERENT PERSON.
 *
 * `RELATION_CHOICES` above was written for the emergency-contact question,
 * where the answer is always a grown-up who can be telephoned — so it has Mom
 * and Grandma and no Son at all. Adding somebody to a household is the other
 * direction entirely: the commonest answer by far is a child, and a list that
 * cannot say "Daughter" would push every parent onto "Someone else", which
 * writes no row and teaches the engine nothing.
 *
 * So: the full family, gendered, commonest first. Every chip but the last
 * writes a `Contact_Relationships` row, and the more of those exist the better
 * every one of these lists gets next year.
 */
export const ADD_PERSON_CHOICES: RelationChoice[] = [
  { relationshipId: 6, gender: 'male', label: 'Son' },
  { relationshipId: 6, gender: 'female', label: 'Daughter' },
  { relationshipId: 1, gender: 'female', label: 'Wife' },
  { relationshipId: 1, gender: 'male', label: 'Husband' },
  { relationshipId: 5, gender: 'female', label: 'Mom' },
  { relationshipId: 5, gender: 'male', label: 'Dad' },
  { relationshipId: 2, gender: 'female', label: 'Sister' },
  { relationshipId: 2, gender: 'male', label: 'Brother' },
  { relationshipId: 41, gender: 'male', label: 'Stepson' },
  { relationshipId: 41, gender: 'female', label: 'Stepdaughter' },
  { relationshipId: 40, gender: 'female', label: 'Stepmom' },
  { relationshipId: 40, gender: 'male', label: 'Stepdad' },
  { relationshipId: 12, gender: 'male', label: 'Grandson' },
  { relationshipId: 12, gender: 'female', label: 'Granddaughter' },
  { relationshipId: 13, gender: 'female', label: 'Grandma' },
  { relationshipId: 13, gender: 'male', label: 'Grandpa' },
  { relationshipId: 3, gender: 'male', label: 'Nephew' },
  { relationshipId: 3, gender: 'female', label: 'Niece' },
  { relationshipId: 4, gender: 'female', label: 'Aunt' },
  { relationshipId: 4, gender: 'male', label: 'Uncle' },
  { relationshipId: 30, gender: null, label: 'Guardian' },
  { relationshipId: 9, gender: null, label: 'Friend' },
  { relationshipId: 17, gender: null, label: 'Neighbor' },
  // No "Someone else" here, unlike RELATION_CHOICES. On the emergency-contact
  // question that phrase is a real answer — it writes Other into MP's own
  // dropdown. Here it wrote nothing at all: a chip that looked like an answer,
  // recorded no relationship, and left the page looking answered. Skipping is
  // the same outcome and says so.
];

/* ------------------------------------------------------------------ */
/* Sections, in MP's own words                                         */
/* ------------------------------------------------------------------ */

/**
 * `Relationship_Types`, as Woodside ordered them.
 *
 * Twenty-three chips in one wall is a wall. These are the church's own
 * groupings rather than invented ones, which matters twice: the headings are
 * words staff already use, and a relationship added in MP lands in a section
 * here without anyone editing this file.
 *
 * A NOTE ON DIRECTION, because it looks wrong until it does not. The stored
 * relationship is the INVERSE of the chip: "this person is my Son" is written
 * as *I am Parent of them* (relationship 6), because MP's Male_Label and
 * Female_Label describe the SUBJECT of the row and the subject is the viewer.
 * The type is looked up from the id that actually gets written, and Son still
 * lands in Primary family — both halves of a relationship share a type.
 */
export const RELATIONSHIP_TYPES: Record<number, { name: string; sort: number }> = {
  1: { name: 'Primary family', sort: 1 },
  2: { name: 'Extended family', sort: 2 },
  3: { name: 'In-laws', sort: 3 },
  4: { name: 'Step & foster', sort: 4 },
  5: { name: 'Business & professional', sort: 5 },
  6: { name: 'Emergency & care', sort: 6 },
  7: { name: 'Other', sort: 99 },
};

/** Relationship_ID → Relationship_Type_ID, read from MP. */
const TYPE_OF: Record<number, number> = {
  1: 1, // Married to
  2: 1, // Sibling of
  5: 1, // Child of
  6: 1, // Parent of
  3: 2, // Aunt/Uncle of
  4: 2, // Niece/Nephew of
  12: 2, // Grandparent of
  13: 2, // Grandchild of
  40: 4, // Step-child of
  41: 4, // Step-parent of
  9: 7, // Friend of
  17: 7, // Neighbor of
  30: 7, // Legal Ward of
};

export interface ChoiceSection {
  name: string;
  choices: RelationChoice[];
}

/**
 * The add-person chips, in sections, in MP's order.
 *
 * Anything whose type we do not hold falls into Other rather than vanishing —
 * a chip nobody can see is worse than a chip in the wrong group.
 */
export function sectionedChoices(choices: RelationChoice[] = ADD_PERSON_CHOICES): ChoiceSection[] {
  const byType = new Map<number, RelationChoice[]>();
  for (const choice of choices) {
    const typeId =
      (choice.relationshipId != null ? TYPE_OF[choice.relationshipId] : undefined) ?? 7;
    byType.set(typeId, [...(byType.get(typeId) ?? []), choice]);
  }
  return [...byType.entries()]
    .sort((a, b) => (RELATIONSHIP_TYPES[a[0]]?.sort ?? 99) - (RELATIONSHIP_TYPES[b[0]]?.sort ?? 99))
    .map(([typeId, list]) => ({
      name: RELATIONSHIP_TYPES[typeId]?.name ?? 'Other',
      choices: list,
    }));
}

/**
 * A first name out of however the name arrived.
 *
 * "Wirgau, Sarah" is MP's sort key and it reaches this form as the stored
 * answer on older drafts; the part after the comma is the first name. Anything
 * else is read left to right.
 */
export function firstNameOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  const comma = trimmed.indexOf(',');
  if (comma > -1)
    return (
      trimmed
        .slice(comma + 1)
        .trim()
        .split(' ')[0] || trimmed
    );
  // split always yields at least one piece, but say so rather than assert it.
  return trimmed.split(' ')[0] ?? trimmed;
}
